import { ChevronRight, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useRegions } from '../hooks/useRegions';
import type { Region, RegionSelector } from '../types/regions.types';
import { RegionSelectorLabels } from '../types/regions.types';
import { useAuthorization } from '@/core/hooks/useAuthorization';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

interface TreeNodeProps {
  region: Region;
  level?: number;
  expandedNodes: Set<string>;
  onToggle: (regionId: string) => void;
  onViewDetail?: (regionId: string) => void;
  onEdit?: (regionId: string) => void;
  onToggleStatus?: (regionId: string, regionName: string, currentIsActive: boolean) => void;
  onAddChild?: (regionId: string) => void;
  onDelete?: (regionId: string, regionName: string) => void;
}

const PADDING_MAP: Record<number, string> = {
  0: 'pl-3',
  1: 'pl-7',
  2: 'pl-11',
  3: 'pl-15',
  4: 'pl-19',
  5: 'pl-23',
};

export function TreeNode({
  region,
  level = 0,
  expandedNodes,
  onToggle,
  onViewDetail,
  onEdit,
  onToggleStatus,
  onAddChild,
  onDelete,
}: TreeNodeProps) {
  const { hasPermission } = useAuthorization();
  const canToggle = hasPermission(PERMISSIONS.REGIONS_TOGGLE);

  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const isExpanded = expandedNodes.has(region.id);

  const { regions: children, isLoading: isLoadingChildren } = useRegions(
    isExpanded && !childrenLoaded
      ? { parentId: region.id, pageSize: 100 }
      : { parentId: region.id, pageSize: 100 },
  );

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(region.id);
    if (isExpanded && !childrenLoaded) {
      setChildrenLoaded(true);
    }
  };

  const paddingClass = PADDING_MAP[level] || 'pl-3';

  return (
    <div className="w-full">
      <div
        className={`flex items-center gap-2 py-2 px-3 hover:bg-select-background rounded-md transition-colors ${paddingClass}`}
      >
        <UnifiedButton
          data-testid={`tree-toggle-${region.id}`}
          title={isExpanded ? 'Colapsar' : 'Expandir'}
          onClick={handleToggle}
          icon={
            <ChevronRight
              size={16}
              className={`text-primary transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          }
        />

        <div className="flex-1 flex items-center gap-4 min-w-0">
          <span className="text-light text-sm font-medium truncate">{region.name}</span>

          <span className="text-primary text-xs bg-primary-background px-2 py-1 rounded">
            {RegionSelectorLabels[region.selector as RegionSelector] || region.selector}
          </span>

          {region.countryNames && region.countryNames.length > 0 && (
            <span className="text-primary text-xs">{region.countryNames.join(', ')}</span>
          )}

          <Switch
            checked={region.isActive}
            disabled={!canToggle}
            onCheckedChange={() => onToggleStatus?.(region.id, region.name, region.isActive)}
          />
        </div>

        <div className="flex items-center gap-1" data-testid={`region-actions-${region.id}`}>
          <Protected permissions={PERMISSIONS.REGIONS_VIEW}>
            <UnifiedButton
              data-testid={`view-region-${region.id}`}
              title="Ver detalle"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetail?.(region.id);
              }}
              icon={<Eye size={16} />}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.REGIONS_EDIT}>
            <UnifiedButton
              data-testid={`edit-region-${region.id}`}
              title="Editar región"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(region.id);
              }}
              icon={<Pencil size={16} />}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.REGIONS_CREATE}>
            <UnifiedButton
              data-testid={`add-child-region-${region.id}`}
              title="Agregar región hija"
              onClick={(e) => {
                e.stopPropagation();
                onAddChild?.(region.id);
              }}
              icon={<Plus size={16} />}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.REGIONS_DELETE}>
            {!region.isActive ? (
              <UnifiedButton
                data-testid={`delete-region-${region.id}`}
                title="Eliminar"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(region.id, region.name);
                }}
                icon={<Trash2 size={16} />}
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <UnifiedButton
                      data-testid={`delete-region-disabled-${region.id}`}
                      title="Eliminar"
                      disabled
                      icon={<Trash2 size={16} />}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Debe deshabilitarse antes de eliminar.</TooltipContent>
              </Tooltip>
            )}
          </Protected>
        </div>
      </div>

      {isExpanded && (
        <div className="pl-4 border-l-2 border-outline ml-6">
          {isLoadingChildren ? (
            <div className="py-2 px-3" aria-hidden />
          ) : children.length > 0 ? (
            children.map((child) => (
              <TreeNode
                key={child.id}
                region={child}
                level={level + 1}
                expandedNodes={expandedNodes}
                onToggle={onToggle}
                onViewDetail={onViewDetail}
                onEdit={onEdit}
                onToggleStatus={onToggleStatus}
                onAddChild={onAddChild}
                onDelete={onDelete}
              />
            ))
          ) : (
            <div className="py-2 px-3 text-primary text-sm">No hay regiones hijas</div>
          )}
        </div>
      )}
    </div>
  );
}
